import EventEmitter from "events";
import AgoraRTC from "agora-rtc-sdk-ng";
import * as Sentry from "@sentry/react";
// import { eventRoomStore } from "@/stores/event";

export type DeviceInfo = {
  cameraCanUse: boolean;
  microphoneCanUse: boolean;
  devices: Device[];
};

export interface AgoraStreamSpec {
  streamID: number | string;
  video: boolean;
  audio: boolean;
  mirror?: boolean;
  screen?: boolean;
  microphoneId?: any;
  cameraId?: any;
  isInjectedStream?: boolean;
  audioOutput?: {
    volume: number;
    deviceId: string;
  };
  audioSource?: any;
  optimizationMode?: string;
}

const streamEvents: string[] = [
  "accessAllowed",
  "accessDenied",
  "track-ended",
  "videoTrackEnded",
  "audioTrackEnded",
  "player-status-changed",
];

const clientEvents: string[] = [
  //"stream-published",
  //"stream-added",
  "user-joined",
  "user-left",
  "user-info-updated",
  "user-published",
  "user-unpublished",
  //"stream-removed",
  //"stream-subscribed",
  //"peer-online",
  //"peer-leave",
  //"mute-audio",
  //"unmute-audio",
  //"mute-video",
  //"unmute-video",
  //"error",
  "network-type-changed",
  "network-quality",
  //"exception",
  //"onTokenPrivilegeWillExpire",
  //"onTokenPrivilegeDidExpire",
  //"streamInjectedStatus",
  //"inject-streaming-disconnected",
  //"network-quality",
  "connection-state-change",
];

export class AgoraRTCClient {
  private streamID: any;
  public _init: boolean = false;
  public _joined: boolean = false;
  public _published: boolean = false;
  private _internalTimer: NodeJS.Timeout | any;
  public _client: any = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
  public _bus: EventEmitter = new EventEmitter();
  public _localStream: any = null;
  public _localScreenShareStream: any = null;
  public _localStreamVideoTrack: any = null;
  public _localStreamAudioTrack: any = null;
  public _streamEvents: string[];
  public _clientEvents: string[];
  public _addEventListener: boolean = false;
  public eventRoomStore: any;
  private _currentMicrophoneId: any = null;

  constructor(deps?: { eventRoomStore: any }) {
    this.streamID = null;
    this._streamEvents = [];
    this._clientEvents = [];
    this.eventRoomStore = deps?.eventRoomStore;
  }

  destroyClient(): void {
    this.unsubscribeClientEvents();
  }

  subscribeClientEvents() {
    AgoraRTC.onAutoplayFailed = () => {
      this.eventRoomStore.updateAutoPlayFailedStatus(true);
    };

    if (this._addEventListener) return;
    this._addEventListener = true;
    for (let evtName of clientEvents) {
      this._clientEvents.push(evtName);
      this._client.on(evtName, (arg1: any, arg2: any) => {
        this._bus.emit(evtName, [arg1, arg2]);
      });
    }
  }

  unsubscribeClientEvents() {
    if (this._addEventListener) {
      for (let evtName of this._clientEvents) {
        this._client.off(evtName, () => {});
        this._clientEvents = this._clientEvents.filter(
          (it: any) => it === evtName
        );
      }
      this._addEventListener = false;
    }
  }

  subscribeLocalStreamEvents() {
    for (let evtName of streamEvents) {
      this._streamEvents.push(evtName);
      this._localStream.on(evtName, (args: any) => {
        this._bus.emit(evtName, args);
      });
    }
  }

  unsubscribeLocalStreamEvents() {
    if (this._localStream) {
      for (let evtName of this._streamEvents) {
        this._localStream.removeEventListener(evtName, (args: any[]) => {});
        this._streamEvents = this._streamEvents.filter(
          (it: any) => it === evtName
        );
      }
    }
  }

  renewToken(newToken: string) {
    if (!this._client)
      return console.warn(
        "renewToken is not permitted, checkout your instance"
      );
    this._client.renewToken(newToken);
  }

  removeAllListeners() {
    this.unsubscribeClientEvents();
    this._bus.removeAllListeners();
  }

  // subscribe
  on(evtName: string, cb: (args: any) => void) {
    this._bus.on(evtName, cb);
  }

  // unsubscribe
  off(evtName: string, cb: (args: any) => void) {
    this._bus.off(evtName, cb);
  }

  // Wait until the underlying Agora client is actually CONNECTED (the SDK's own
  // connectionState — the source of truth; NOT the store's rtc.joined flag, which
  // pre-show.tsx:105 proved can say "joined" while the websocket isn't ready).
  // Poll rather than wire another connection-state-change listener, to avoid
  // interfering with the existing subscribeClientEvents() plumbing.
  async _waitForConnected(timeoutMs = 10000): Promise<boolean> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (this._client?.connectionState === "CONNECTED") return true;
      await new Promise((r) => setTimeout(r, 200));
    }
    return this._client?.connectionState === "CONNECTED";
  }

  // Single-flight: publish() can now WAIT for the join to complete, which opens a
  // window where a second concurrent publish() would double-fire into the SDK
  // (INVALID_OPERATION). First caller wins; others no-op (same contract as the
  // old optimistic-resolve behavior).
  private _publishing: boolean = false;

  async publish() {
    if (this._published) {
      return "";
    }
    if (this._publishing) {
      console.log("[rtc-guard] publish skipped — another publish in flight");
      return "";
    }
    this._publishing = true;
    try {
      // Never fire publish into a client that hasn't finished joining — that's the
      // "Can't publish stream, haven't joined yet!" / WS_ABORT race seen on the
      // 2026-06-16/23/24 residence-host traces and the 2026-07-04 multi visit.
      // Wait for the real join to complete instead of failing (the residence's
      // audio then goes out, instead of "mic shown open but inaudible").
      if (this._client?.connectionState !== "CONNECTED") {
        const waitStart = Date.now();
        console.warn("[rtc-guard] publish deferred — waiting for CONNECTED", { state: this._client?.connectionState });
        const ok = await this._waitForConnected();
        if (!ok) {
          console.error("[rtc-guard] publish DROPPED — never reached CONNECTED", { state: this._client?.connectionState, waitedMs: Date.now() - waitStart });
          Sentry.captureMessage("[rtc-guard] publish dropped: not connected", "warning");
          return "";
        }
        console.log("[rtc-guard] connection ready — publishing now", { waitedMs: Date.now() - waitStart });
      }
      AgoraRTC.enableLogUpload();
      AgoraRTC.setLogLevel(0);
      // _localStream can contain null/undefined slots: createLocalStream builds
      // [audioTrack, videoTrack] and a failed camera creation is caught+swallowed
      // (videoTrack stays undefined); the injected path can yield [null, track].
      // The NG SDK rejects any non-track entry with INVALID_PARAMS "parameter is
      // not local track" (TM-FRONTEND-2M) — publish only the real tracks.
      const tracksToPublish = Array.isArray(this._localStream)
        ? this._localStream.filter((t: any) => t !== null && t !== undefined)
        : this._localStream;
      if (!tracksToPublish || (Array.isArray(tracksToPublish) && tracksToPublish.length === 0)) {
        console.warn("[rtc-guard] publish skipped — no valid local tracks", { raw: Array.isArray(this._localStream) ? this._localStream.length : typeof this._localStream });
        Sentry.captureMessage("[rtc-guard] publish skipped: no valid local tracks", "warning");
        return "";
      }
      // NG SDK: publish() returns a promise (the old error-callback 2nd arg was
      // ignored by the SDK, and the 300ms optimistic resolve marked _published
      // even when the real publish had failed — the UI lied).
      await this._client.publish(tracksToPublish);
      this._published = true;
      console.log("[rtc-guard] publish OK", { state: this._client?.connectionState });
      return "";
    } catch (err) {
      console.error("[rtc-guard] publish FAILED (not marked published)", err);
      Sentry.captureException(err, { tags: { component: "agora-rtc", op: "publish" } });
      throw err;
    } finally {
      this._publishing = false;
    }
  }

  async setClientRole(role: any) {
    this._client.setClientRole(role);
  }

  async unpublish(type = "") {
    if (!this._published || !this._localStream) {
      return "";
    }
    // "Cut the mic" MUST take effect locally no matter what the network says:
    // closing the local track stops transmission even if the unpublish RPC can't
    // run. So: only attempt the network op when actually CONNECTED, and destroy
    // the local stream in every path.
    if (this._client?.connectionState === "CONNECTED") {
      try {
        await this._client.unpublish(this._localStream);
        console.log("[rtc-guard] unpublish OK (network + local)", { type });
      } catch (err) {
        console.error("[rtc-guard] unpublish network op FAILED — destroying local track anyway", err);
        Sentry.captureException(err, { tags: { component: "agora-rtc", op: "unpublish" } });
      }
    } else {
      console.warn("[rtc-guard] unpublish: not connected — skipping network op, destroying local track (mic cut takes effect locally)", { state: this._client?.connectionState, type });
      Sentry.addBreadcrumb({ category: "agora-rtc", message: "unpublish while not connected — local destroy only", level: "info" });
    }
    this.destroyLocalStream(type);
    this._published = false;
    return "";
  }

  setRemoteVideoStreamType(stream: any, streamType: number) {
    // Cosmetic quality hint — safe to skip while the websocket isn't ready
    // (was throwing WS_ABORT from pre-show.tsx:105; callers re-invoke on every
    // stream update so the hint is re-applied once connected).
    if (this._client?.connectionState !== "CONNECTED") {
      console.warn("[rtc-guard] setRemoteVideoStreamType skipped — not connected", { state: this._client?.connectionState });
      return;
    }
    this._client.setRemoteVideoStreamType(stream, streamType);
  }

  async enableDualStream() {
    return new Promise<void>((resolve, reject) => {
      // this._client.enableDualStream(() => {
      //   const lowStreamParam = [244, 144, 15, 140];
      //   this._client.setLowStreamParameter({
      //     width: lowStreamParam[0],
      //     height: lowStreamParam[1],
      //     framerate: lowStreamParam[2],
      //     bitrate: lowStreamParam[3],
      //   });
      //   resolve();
      // }, reject);
      const lowStreamParam = this.eventRoomStore.getAgoraLowStreamProfile();
      try {
        this._client.setLowStreamParameter({
          width: lowStreamParam[0],
          height: lowStreamParam[1],
          framerate: lowStreamParam[2],
          bitrate: lowStreamParam[3],
        });
        this._client.enableDualStream();
        resolve();
      } catch (e) {
        console.error(e);
        reject();
      }
    });
  }

  async createScreenShareStream(data: AgoraStreamSpec): Promise<any> {
    //this._localStream = AgoraRTC.createStream({...data, mirror: false});
    //this._localStream.setVideoProfile("720p_1");
    this._localStream = await AgoraRTC.createScreenVideoTrack(
      {
        encoderConfig: "1080p_1",
      },
      "auto"
    );
    return this._localStream;
    // this._localStream.setVideoEncoderConfiguration({
    //   resolution: {
    //     width: 1280,
    //     height: 720
    //   },
    //   frameRate: {
    //     min: 15,
    //     max: 30
    //   },
    //   bitrate: {
    //     min: 800,
    //     max: 2000
    //   }
    // });
    // return new Promise((resolve, reject) => {
    //   this._localStream.init(
    //     () => {
    //       this.streamID = data.streamID;
    //       this.subscribeLocalStreamEvents();
    //       if (data.audioOutput && data.audioOutput.deviceId) {
    //         this.setAudioOutput(data.audioOutput.deviceId)
    //           .then(() => {
    //             console.log("setAudioOutput success", data.audioOutput);
    //           })
    //           .catch((err: any) => {
    //             console.warn("setAudioOutput failed", err, JSON.stringify(err));
    //           });
    //       }
    //       resolve(this._localStream);
    //     },
    //     (err: any) => {
    //       if ((err.msg === 'NotAllowedError' || err.msg === 'Error')) {
    //         alert('Permission not allowed')
    //       } else if (err.msg === 'NotFoundError') {
    //         alert('Could not find any media device')
    //       } else if (err.msg === 'NotReadableError') {
    //         alert('Hardware error occur, Please try refreshing the page or updating the device driver');
    //       }
    //       reject(err);
    //     }
    //   );
    // });
  }

  async createLocalStream(data: AgoraStreamSpec, enableNoiseSuppression: boolean = true): Promise<any> {
    // Store the microphone ID for later use
    this._currentMicrophoneId = data.microphoneId;

    // If no video is required
    if (!data.video) {
      this._localStreamAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        microphoneId: data.microphoneId,
        AEC: true, // acoustic echo cancellation
        // AGC: false — Since Agora SDK 4.24.x, enabling ANS triggers AI noise
        // suppression (AINS), which caps AGC max gain from 24dB to 12dB and
        // makes the speaker (esp. guides on headset/Bluetooth mics) sound
        // significantly quieter to listeners. Disabling AGC keeps the mic at
        // its natural gain while still letting ANS strip background noise.
        // Docs: https://docs.agora.io/en/voice-calling/advanced-features/ai-noise-suppression
        AGC: false,
        ANS: enableNoiseSuppression, // automatic noise suppression
        encoderConfig: "speech_standard",
      });
  
      // We only need the audio track
      this._localStream = [this._localStreamAudioTrack];
      return this._localStream;
    }
  
    // Otherwise, create both audio and video tracks
    this._localStreamAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
      microphoneId: data.microphoneId,
      AEC: true,
      // AGC: false — see explanation in the audio-only branch above.
      AGC: false,
      ANS: enableNoiseSuppression,
      encoderConfig: "speech_standard",
    });
  
    try {
      this._localStreamVideoTrack = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: await this.eventRoomStore.getAgoraVideoProfile(),
        optimizationMode: data.optimizationMode,
        cameraId: data.cameraId,
      });
    } catch (e) {
      this.eventRoomStore.handleDeviceError();
    }
  
    // If it's an injected stream, just return the video track
    if (data.isInjectedStream) {
      this._localStream = [this._localStreamVideoTrack];
      return [null, this._localStreamVideoTrack];
    }
  
    // Return both audio and video tracks
    this._localStream = [
      this._localStreamAudioTrack,
      this._localStreamVideoTrack,
    ];
    return this._localStream;
  }

  destroyLocalStream(type = "") {
    this.unsubscribeLocalStreamEvents();
    if (this._localStream) {
      // if (this._localStream.isPlaying()) {
      //   this._localStream.stop();
      // }
      // this._localStream.close();
      if (type == "screenShare") {
        if (Array.isArray(this._localStream)) {
          if (typeof this._localStream[0] != "undefined") {
            this._localStream[0].stop();
            this._localStream[0].close();
          }

          if (typeof this._localStream[1] != "undefined") {
            this._localStream[1].stop();
            this._localStream[1].close();
          }
        } else {
          this._localStream.stop();
          this._localStream.close();
        }

        if (this._localStream?.stream?.audioTrack.isPlaying) {
          this._localStream.stream.audioTrack.stop();
          this._localStream.stream.audioTrack.close();
        }
      } else {
        if (this._localStream?.stream?.videoTrack.isPlaying) {
          this._localStream.stream.videoTrack.stop();
          this._localStream.stream.videoTrack.close();
        }

        if (this._localStream?.stream?.audioTrack.isPlaying) {
          this._localStream.stream.audioTrack.stop();
          this._localStream.stream.audioTrack.close();
        }
      }
    }
    this._localStream = null;
    this.streamID = 0;
  }

  async join(appId: string, token?: string, channel?: string, uid?: number) {
    // return new Promise((resolve, reject) => {
    //   this._client.join(appId, token, channel, uid, resolve, reject);
    // });
    try {
      this.subscribeClientEvents();
      const result = await this._client.join(appId, channel, token, uid);
      this._joined = true; // was a dead flag (never set) — now reflects reality
      console.log("[rtc-guard] join OK", { channel, uid, state: this._client?.connectionState });
      return result;
    } catch (e) {
      // A swallowed join failure lets the app mark itself "joined" with no
      // media and no network-quality events — every caller must handle this.
      this._joined = false;
      console.error("[rtc-guard] join FAILED", { channel, uid, error: e });
      Sentry.captureException(e, { tags: { component: "agora-rtc", op: "join" } });
      throw e;
    }
  }

  async leave() {
    this._joined = false;
    if (this._client) {
      return new Promise((resolve, reject) => {
        this._client.leave(resolve, reject);
      });
    }
  }

  async initVolumeIndicator() {
    AgoraRTC.setParameter('AGORA_VOLUME_INDICATION_INTERVAL', 200)
    this._client.enableAudioVolumeIndicator()
    this._client.on('volume-indicator', (volumes: any) => {
      volumes.forEach((volume: any) => {
        let videoItems = document.getElementsByClassName(`side-stream ${volume.uid}`);
          for (let i = 0; i < videoItems.length; i++) {
            let videoItem = videoItems[i] as HTMLElement;
            if (volume.level >= 40) {
              videoItem.classList.add('active-speaker');
            } else {
              videoItem.classList.remove('active-speaker');
            }
          }
      })
    })
  }

  setAudioOutput(speakerId: string) {
    return new Promise((resolve, reject) => {
      if (this._client) {
        this._client.setAudioOutput(speakerId, resolve, reject);
        return;
      }
      resolve("");
    });
  }

  setAudioVolume(volume: number) {
    this._client.setAudioVolume(volume);
  }

  async updateNoiseSuppression(enable: boolean, microphoneId?: string): Promise<void> {
    if (!this._localStreamAudioTrack) {
      return;
    }

    const currentMicId = microphoneId || this._currentMicrophoneId;
    const wasPublished = this._published;

    // Unpublish if currently published
    if (wasPublished && this._localStream) {
      try {
        await this._client.unpublish(this._localStreamAudioTrack);
      } catch (err) {
        console.warn("Error unpublishing audio track:", err);
      }
    }

    // Close the old audio track
    this._localStreamAudioTrack.close();

    // Create new audio track with updated noise suppression setting
    this._localStreamAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
      microphoneId: currentMicId,
      AEC: true,
      // AGC: false — see explanation in createLocalStream above.
      // Critical here too: this code path runs every time the guide toggles
      // noise cancellation. Keeping AGC:false makes the toggle a pure ANS
      // change without an extra ~6dB volume hit.
      AGC: false,
      ANS: enable, // Apply the new noise suppression setting
      encoderConfig: "speech_standard",
    });

    // Update the local stream array
    if (Array.isArray(this._localStream)) {
      if (this._localStreamVideoTrack) {
        // If we have both audio and video
        this._localStream = [this._localStreamAudioTrack, this._localStreamVideoTrack];
      } else {
        // Audio only
        this._localStream = [this._localStreamAudioTrack];
      }
    }

    // Republish if it was published before
    if (wasPublished) {
      try {
        await this._client.publish(this._localStreamAudioTrack);

        // CRITICAL: Re-apply muted state if the user had their mic disabled.
        // Use setMuted (track stays published, instant) rather than setEnabled
        // (which would unpublish + republish again, defeating the noise
        // suppression toggle and causing perceived silence).
        const localStream = this.eventRoomStore?._state?.rtc?.localStream;
        if (localStream && localStream.audio === false && this._localStreamAudioTrack) {
          await this._localStreamAudioTrack.setMuted(true);
        }
      } catch (err) {
        console.error("Error republishing audio track:", err);
      }
    }
  }

  async subscribe(remoteUser: any, mediaType: any) {
    return await this._client.subscribe(remoteUser, mediaType);
  }

  destroy(type = ""): void {
    this._internalTimer && clearInterval(this._internalTimer);
    this._internalTimer = null;
    this.destroyLocalStream(type);
    this.removeAllListeners();
  }

  async exit() {
    try {
      await this.leave();
    } catch (err) {
      throw err;
    } finally {
      this.destroy();
    }
  }

  getDevices(): Promise<DeviceInfo> {
    return new Promise((resolve, reject) => {
      AgoraRTC.getDevices()
        .then((devices: any) => {
          const _devices: any[] = [];
          devices.forEach((item: any) => {
            _devices.push({
              deviceId: item.deviceId,
              kind: item.kind,
              label: item.label,
            });
          });
          resolve({
            cameraCanUse:
              devices.filter((it: any) => it.kind === "videoinput").length > 0
                ? true
                : false,
            microphoneCanUse:
              devices.filter((it: any) => it.kind === "audioinput").length > 0
                ? true
                : false,
            devices: _devices,
          });
        })
        .catch((err: any) => {
          reject(err);
        });
      // AgoraRTC.getDevices(
      //   (devices: any) => {
      //     const _devices: any[] = [];
      //     devices.forEach((item: any) => {
      //       _devices.push({
      //         deviceId: item.deviceId,
      //         kind: item.kind,
      //         label: item.label
      //       });
      //     });
      //     resolve({
      //       cameraCanUse:
      //         devices.filter((it: any) => it.kind === "videoinput").length > 0
      //           ? true
      //           : false,
      //       microphoneCanUse:
      //         devices.filter((it: any) => it.kind === "audioinput").length > 0
      //           ? true
      //           : false,
      //       devices: _devices
      //     });
      //   },
      //   (err: any) => {
      //     reject(err);
      //   }
      // );
    });
  }

  async forceGetDevices(): Promise<DeviceInfo> {
    let audioPermissionOK = null;
    let videoPermissionOK = null;
    let tempAudioStream = "" as any;
    let tempVideoStream = "" as any;
    try {
      tempAudioStream = await AgoraRTC.createMicrophoneAudioTrack();
    } catch (e) {
      audioPermissionOK = "";
    }

    try {
      tempVideoStream = await AgoraRTC.createCameraVideoTrack();
    } catch (e) {
      videoPermissionOK = "";
    }

    // const audioPermissionOK = new Promise(resolve => {
    //   tempAudioStream.init(
    //     () => resolve(null),
    //     (err: any) => resolve(err)
    //   );
    // });
    // const videoPermissionOK = new Promise(resolve => {
    //   tempVideoStream.init(
    //     () => resolve(null),
    //     (err: any) => resolve(err)
    //   );
    // });

    try {
      let [microphone, camera] = await Promise.all([
        audioPermissionOK,
        videoPermissionOK,
      ]);
      let result = await this.getDevices();

      if (microphone !== null) {
        result.microphoneCanUse = false;
        console.warn("create audio temp stream failed!", microphone);
      }
      if (camera !== null) {
        result.cameraCanUse = false;
        console.warn("create video temp stream failed!", camera);
      }

      return result;
    } catch (err) {
      throw err;
    } finally {
      if (tempAudioStream != "") tempAudioStream.close();
      if (tempVideoStream != "") tempVideoStream.close();
      // if (tempAudioStream.initialized) tempAudioStream.close();
      // if (tempVideoStream.initialized) tempVideoStream.close();
    }
  }
}
